<script setup>
import { nextTick, ref, shallowRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { ContractsAPI } from 'dashboard/api/talkviaContracts';
import { useAlert } from 'dashboard/composables';
import ContractSendDialog from '../contracts/ContractSendDialog.vue';

// "Enviar contrato" na Central do cliente: sem contratos pendentes abre direto o assistente
// "Gerar contrato"; com pendentes, permite editar, gerar um novo ou reenviar o link.
const props = defineProps({
  contact: { type: Object, default: () => ({}) },
  conversationId: { type: [Number, String], default: null },
  inboxId: { type: [Number, String], default: null },
});

const copy = {
  title: 'Enviar contrato',
  description:
    'Gere um novo contrato, edite um pendente ou envie o link de assinatura nesta conversa.',
  generate: 'Gerar novo contrato',
  generateHint: 'Abre o assistente com este cliente e esta conversa.',
  pending: 'Contratos pendentes de assinatura',
  untitled: 'Contrato',
  createdOn: 'Criado em',
  noValue: 'Valor não informado',
  edit: 'Editar',
  editLabel: 'Editar contrato',
  sendLink: 'Enviar link',
  sendLinkLabel: 'Enviar link de assinatura de',
  close: 'Fechar',
  separator: '·',
  loadFailed:
    'Não foi possível carregar os contratos deste cliente. Tente novamente.',
  sent: 'Link enviado na conversa.',
  sendFailed: 'Não foi possível enviar. Confira os dados e tente novamente.',
};

const route = useRoute();
const router = useRouter();
const dialogRef = ref(null);
const sendDialogRef = ref(null);
const generatorRef = ref(null);
const generatorComponent = shallowRef(null);
const pendingContracts = ref([]);
const isLoading = ref(false);
const isSending = ref(false);

const isOpenPending = contract => {
  if (contract?.status !== 'pending') return false;
  if (!contract.expires_at) return true;
  const expiresAt = new Date(contract.expires_at);
  return Number.isNaN(expiresAt.getTime()) || expiresAt > new Date();
};

const formatDate = value => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR').format(date);
};

const formatValue = contract => {
  const total = Number(contract?.invoice_total || 0);
  if (!(total > 0)) return copy.noValue;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(total);
};

const contractName = contract =>
  contract?.template_name || contract?.product_name || copy.untitled;

// O assistente vive na página de Contratos; carregado só quando for usado.
const openGenerator = async () => {
  if (!generatorComponent.value) {
    generatorComponent.value = (await import('../contracts/Index.vue')).default;
  }
  await nextTick();
  generatorRef.value?.openGenerator({
    contact: props.contact,
    conversationId: props.conversationId,
    inboxId: props.inboxId,
  });
};

const open = async () => {
  if (isLoading.value || !props.contact?.id) return;
  isLoading.value = true;
  let contracts;
  try {
    const { data } = await ContractsAPI.get({
      contact_id: props.contact.id,
      status: 'pending',
    });
    contracts = (data?.contracts || []).filter(isOpenPending);
  } catch {
    useAlert(copy.loadFailed);
    return;
  } finally {
    isLoading.value = false;
  }

  pendingContracts.value = contracts;
  if (!contracts.length) {
    await openGenerator();
    return;
  }
  dialogRef.value?.open();
};

const generateNew = async () => {
  dialogRef.value?.close();
  await openGenerator();
};

const editPendingContract = contract => {
  dialogRef.value?.close();
  router.push({
    name: 'talkvia_contracts',
    params: { accountId: route.params.accountId },
    query: { edit_contract_id: contract.id },
  });
};

const sendPendingContract = contract => {
  dialogRef.value?.close();
  sendDialogRef.value?.open(
    contract,
    props.inboxId ? { inboxId: props.inboxId } : {}
  );
};

const handleSendContractLink = async ({ contract, payload }) => {
  if (isSending.value) return;
  isSending.value = true;
  try {
    // Na caixa desta conversa, o link vai exatamente para ela.
    const isThisInbox =
      props.conversationId &&
      Number(payload?.inbox_id) === Number(props.inboxId);
    await ContractsAPI.sendLink(
      contract.id,
      isThisInbox
        ? { ...payload, conversation_id: props.conversationId }
        : payload
    );
    sendDialogRef.value?.close();
    useAlert(copy.sent);
  } catch (error) {
    useAlert(error?.response?.data?.error || copy.sendFailed);
  } finally {
    isSending.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <slot :open="open" :is-loading="isLoading" />
  <Dialog
    ref="dialogRef"
    width="md"
    :title="copy.title"
    :description="copy.description"
    :cancel-button-label="copy.close"
    :show-confirm-button="false"
  >
    <div class="grid gap-4">
      <button
        type="button"
        class="flex w-full items-center gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3 text-left transition hover:border-n-brand hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
        data-test="generate-contract"
        @click="generateNew"
      >
        <span
          class="grid size-9 shrink-0 place-content-center rounded-lg bg-n-brand/10 text-n-brand"
        >
          <span class="i-hugeicons-file-add size-5" aria-hidden="true" />
        </span>
        <span class="min-w-0">
          <span class="block text-sm font-semibold text-n-slate-12">
            {{ copy.generate }}
          </span>
          <span class="block text-xs text-n-slate-11">
            {{ copy.generateHint }}
          </span>
        </span>
      </button>

      <section class="grid gap-2" :aria-label="copy.pending">
        <h3 class="mb-0 text-xs font-semibold text-n-slate-11">
          {{ copy.pending }}
        </h3>
        <ul class="m-0 grid list-none gap-2 p-0">
          <li
            v-for="contract in pendingContracts"
            :key="contract.id"
            class="flex flex-col items-stretch justify-between gap-3 rounded-xl border border-n-weak px-3 py-2.5 sm:flex-row sm:items-center"
            data-test="pending-contract"
          >
            <div class="min-w-0">
              <p
                class="mb-0 truncate text-sm font-medium text-n-slate-12"
                :title="contractName(contract)"
              >
                {{ contractName(contract) }}
              </p>
              <p class="mb-0 mt-0.5 text-xs text-n-slate-11">
                {{ copy.createdOn }} {{ formatDate(contract.created_at) }}
                <span aria-hidden="true">{{ copy.separator }}</span>
                <span class="tabular-nums">{{ formatValue(contract) }}</span>
              </p>
            </div>
            <div class="flex shrink-0 flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                icon="i-hugeicons-file-edit"
                :label="copy.edit"
                :aria-label="`${copy.editLabel} ${contractName(contract)}`"
                class="!min-h-11"
                data-test="edit-pending-contract"
                @click="editPendingContract(contract)"
              />
              <Button
                size="sm"
                variant="faded"
                icon="i-hugeicons-sent"
                :label="copy.sendLink"
                :aria-label="`${copy.sendLinkLabel} ${contractName(contract)}`"
                class="!min-h-11"
                data-test="send-pending-contract"
                @click="sendPendingContract(contract)"
              />
            </div>
          </li>
        </ul>
      </section>
    </div>
  </Dialog>
  <ContractSendDialog
    ref="sendDialogRef"
    :is-sending="isSending"
    @send="handleSendContractLink"
  />
  <component
    :is="generatorComponent"
    v-if="generatorComponent"
    ref="generatorRef"
    generator-only
  />
</template>
